import React, { useMemo, useRef, useState } from "react";

import {
  CellContext,
  Column,
  flexRender,
  getCoreRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  HeaderContext,
  HeaderGroup,
  OnChangeFn,
  Row,
  RowData,
  SortingState,
  Table,
  Cell as TableCell,
  Header as TableHeader,
  TableOptions,
  TableState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { noop, omit, times } from "lodash-es";
import { twJoin, twMerge } from "tailwind-merge";
import { useDebouncedCallback } from "use-debounce";
import { useLocalStorage } from "usehooks-ts";

import { ColumnVisibilityDropdown as ColumnVisibilityDropdownBase } from "src/base-components/ColumnVisibilityDropdown";
import { Skeleton } from "src/design-system/Skeleton";
import { logger } from "src/utils/logger";

// We actually expect any functions here and can not be explicitly typed
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TableActions = Record<string, (...args: any[]) => void>;
type TableVariant = "default" | "compact";

declare module "@tanstack/table-core" {
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface TableMeta<TData extends RowData> {
    actions?: TableActions;
    variant?: TableVariant;
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    displayName?: string | null;
    sticky?: "left" | "right";
    hideSkeleton?: boolean;
  }
}

type RowProps = {
  classNameOverrides?: string;
  [key: string]: unknown;
};
type HeaderGroupProps = {
  classNameOverrides?: string;
  [key: string]: unknown;
};

type Props<T extends object, A extends TableActions> = {
  data: T[];
  columns: TableOptions<T>["columns"];
  frameClassName?: string;
  rowClassName?: string;
  rowPropsGetter?: (rowData: Row<T>) => RowProps;
  headerPropsGetter?: (headerProps: HeaderGroup<T>) => HeaderGroupProps;
  renderTableHeader?: (tableInstance: Table<T>) => React.ReactNode;
  noScroll?: boolean;
  dataLoc?: string;
  isLoading?: boolean;
  actions?: A;
  getRowId?: TableOptions<T>["getRowId"];
  loadingRowsCount?: number;
  onScrolledToTheBottom?: () => void;
  isFetchingNextPage?: boolean;
  variant?: TableVariant;
  globalFilter?: string;
  // Used to store the visual state of the table
  uiStoreKey?: string;
  clientSideFiltering?: boolean;
  initialState?: TableOptions<T>["initialState"];
  rowSelection?: TableState["rowSelection"];
  columnVisibility?: TableState["columnVisibility"];
  onRowSelectionChange?: TableOptions<T>["onRowSelectionChange"];
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
  disableVirtualization?: boolean;
  emptyState?: React.ReactNode;
  virtualizeColumns?: boolean;
};

const ROW_CLASS_NAMES =
  "[&:not(:last-child)]:border-b group group/row border-gray-100";
const HEADER_CLASS_NAMES =
  "sticky top-0 bg-opacity-100 bg-white border-b border-gray-100 font-inter-medium-12px text-left z-20";

const SkeletonCell: React.FC<{
  hideSkeleton: boolean;
  variant: TableVariant;
}> = ({ hideSkeleton, variant }) => (
  <td className="relative h-[inherit] p-0">
    {!hideSkeleton && (
      <div
        className={twJoin(
          "flex items-center p-1.5",
          variant === "compact" ? "min-h-[32px]" : "min-h-[48px]",
        )}
      >
        <Skeleton height="h-5" width="w-4/5" />
      </div>
    )}
  </td>
);

// Dev-only sanity checks for `virtualizeColumns`
const warnIfVirtualizationMisused = <T,>(
  virtualizeColumns: boolean,
  visibleColumns: Column<T, unknown>[],
  headerGroups: HeaderGroup<T>[],
) => {
  if (!import.meta.env.DEV) return;
  if (!virtualizeColumns) return;

  const missing = visibleColumns.filter((c) => c.columnDef.size == null);
  if (missing.length > 0) {
    logger.warn(
      `[TableComp] virtualizeColumns is enabled but ${missing.length} ` +
        `column(s) have no explicit \`size\`. Column virtualization uses ` +
        `\`table-layout: fixed\`; without \`size\` on every column the ` +
        `layout breaks. Add \`size\` to: ${missing
          .map((c) => c.id)
          .join(", ")}`,
    );
  }

  if (headerGroups.length > 1) {
    logger.warn(
      `[TableComp] virtualizeColumns does not support grouped headers ` +
        `(\`columnHelper.group(...)\`). The non-leaf header rows align with ` +
        `group spans, not with leaf columns, so they cannot be virtualized. ` +
        `Group header rows will be skipped; only the leaf header row renders.`,
    );
  }
};

export const TableComp = <T extends object, A extends TableActions>({
  data,
  columns,
  frameClassName,
  rowClassName,
  rowPropsGetter,
  headerPropsGetter,
  noScroll = false,
  isLoading = false,
  loadingRowsCount = 10,
  dataLoc,
  actions,
  getRowId,
  onScrolledToTheBottom,
  isFetchingNextPage,
  variant = "default",
  globalFilter,
  clientSideFiltering = false,
  initialState,
  uiStoreKey,
  rowSelection,
  onRowSelectionChange,
  columnVisibility: defaultColumnVisibility,
  sorting,
  onSortingChange,
  manualSorting,
  disableVirtualization = false,
  emptyState,
  virtualizeColumns = false,
}: Props<T, A>) => {
  const [columnVisibility, setColumnVisibility] = useVisibilityStore(
    uiStoreKey,
    defaultColumnVisibility,
  );
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleSortingChange: OnChangeFn<SortingState> | undefined =
    onSortingChange
      ? (updater) => {
          onSortingChange(updater);
          tableContainerRef.current?.scrollTo({ top: 0 });
        }
      : undefined;

  const tableInstance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: clientSideFiltering
      ? getFilteredRowModel()
      : undefined,
    getFacetedUniqueValues: clientSideFiltering
      ? getFacetedUniqueValues()
      : undefined,
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    defaultColumn: { size: undefined },
    meta: { actions, variant },
    getRowId,
    manualSorting,
    enableSortingRemoval: false,
    state: {
      globalFilter,
      columnVisibility,
      rowSelection,
      ...(sorting !== undefined && { sorting }),
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: handleSortingChange,
    enableRowSelection: !!rowSelection,
    initialState,
    onRowSelectionChange,
  });
  const { getHeaderGroups } = tableInstance;

  const headerGroups = getHeaderGroups();
  const rows = tableInstance.getRowModel().rows;
  const visibleColumns = tableInstance.getVisibleLeafColumns();

  // Position of each visible leaf column inside `row.getVisibleCells()` and
  // `headerGroup.headers` (for a flat / non-grouped header layout). Lets us
  // resolve a `Column` to its row cell or leaf header via O(1) lookups
  // instead of building a fresh Map per row.
  const visibleColIndexById = useMemo(
    () => new Map(visibleColumns.map((col, i) => [col.id, i] as const)),
    [visibleColumns],
  );

  warnIfVirtualizationMisused(virtualizeColumns, visibleColumns, headerGroups);

  // When virtualizing, the non-leaf header groups (group headers, with
  // colSpan > 1) don't align 1:1 with leaf columns and would mis-index
  // against `visibleColIndexById`. Skip them and render only the leaf row.
  const renderedHeaderGroups =
    virtualizeColumns && headerGroups.length > 1
      ? headerGroups.slice(-1)
      : headerGroups;

  // Column virtualization (opt-in). When disabled, the entire visible
  // column set is treated as "non-sticky" so the existing rendering path is
  // exercised unchanged.
  const { stickyLeftColumns, nonStickyColumns, stickyRightColumns } =
    useMemo(() => {
      if (!virtualizeColumns) {
        return {
          stickyLeftColumns: [] as Column<T, unknown>[],
          nonStickyColumns: visibleColumns,
          stickyRightColumns: [] as Column<T, unknown>[],
        };
      }
      return visibleColumns.reduce(
        (acc, col) => {
          switch (col.columnDef.meta?.sticky) {
            case "left":
              acc.stickyLeftColumns.push(col);
              break;
            case "right":
              acc.stickyRightColumns.push(col);
              break;
            default:
              acc.nonStickyColumns.push(col);
          }
          return acc;
        },
        {
          stickyLeftColumns: [] as Column<T, unknown>[],
          nonStickyColumns: [] as Column<T, unknown>[],
          stickyRightColumns: [] as Column<T, unknown>[],
        },
      );
    }, [visibleColumns, virtualizeColumns]);

  const columnVirtualizer = useVirtualizer({
    count: nonStickyColumns.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: (idx) => nonStickyColumns[idx]?.getSize() ?? 100,
    overscan: 6,
    horizontal: true,
    enabled: virtualizeColumns,
  });

  const virtualCols = virtualizeColumns
    ? columnVirtualizer.getVirtualItems()
    : [];
  const totalColSize = virtualizeColumns ? columnVirtualizer.getTotalSize() : 0;
  const colPaddingLeft = virtualCols[0]?.start ?? 0;
  const colPaddingRight =
    totalColSize - (virtualCols[virtualCols.length - 1]?.end ?? 0);

  const renderHeaderCell = (header: TableHeader<T, unknown>) => {
    const sticky = header.column.columnDef.meta?.sticky;
    return (
      <th
        key={header.id}
        className={twJoin(
          "p-0",
          sticky === "left" && "sticky left-0",
          sticky === "right" && "sticky right-0",
        )}
        colSpan={header.colSpan}
        style={{
          minWidth: header.column.columnDef.minSize,
          maxWidth: header.column.columnDef.maxSize,
          width: header.column.columnDef.size ? header.getSize() : undefined,
        }}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </th>
    );
  };

  const renderBodyCell = (cell: TableCell<T, unknown>) => {
    const sticky = cell.column.columnDef.meta?.sticky;
    return (
      // h-[inherit] & p-0 allow the content inside td to take full width/height
      <td
        key={cell.id}
        className={twJoin(
          "h-[inherit] p-0",
          sticky === "left" && "sticky left-0",
          sticky === "right" && "sticky right-0",
        )}
        style={{
          minWidth: cell.column.columnDef.minSize,
          maxWidth: cell.column.columnDef.maxSize,
          width: cell.column.columnDef.size ? cell.column.getSize() : undefined,
        }}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </td>
    );
  };

  const renderHeaderGroup = (headerGroup: HeaderGroup<T>) => {
    const headerProps = headerPropsGetter?.(headerGroup);
    const trProps = {
      className: twMerge(HEADER_CLASS_NAMES, headerProps?.classNameOverrides),
      ...omit(headerProps, "classNameOverrides"),
    };

    if (!virtualizeColumns) {
      return (
        <tr key={headerGroup.id} {...trProps}>
          {headerGroup.headers.map(renderHeaderCell)}
        </tr>
      );
    }

    const headerFor = (col: Column<T, unknown>) => {
      const idx = visibleColIndexById.get(col.id);
      return idx === undefined ? undefined : headerGroup.headers[idx];
    };

    return (
      <tr key={headerGroup.id} {...trProps}>
        {stickyLeftColumns.map((c) => {
          const h = headerFor(c);
          return h ? renderHeaderCell(h) : null;
        })}
        <th
          aria-hidden
          style={{ width: colPaddingLeft, padding: 0 }}
          data-loc="col-virt-left-spacer"
        />
        {virtualCols.map((vc) => {
          const col = nonStickyColumns[vc.index];
          const h = col ? headerFor(col) : undefined;
          return h ? renderHeaderCell(h) : null;
        })}
        <th
          aria-hidden
          style={{ width: colPaddingRight, padding: 0 }}
          data-loc="col-virt-right-spacer"
        />
        {stickyRightColumns.map((c) => {
          const h = headerFor(c);
          return h ? renderHeaderCell(h) : null;
        })}
      </tr>
    );
  };

  const renderRow = (row: Row<T>) => {
    const rowProps = rowPropsGetter?.(row);
    const trProps = {
      className: twMerge(
        ROW_CLASS_NAMES,
        rowClassName,
        rowProps?.classNameOverrides,
      ),
      ...omit(rowProps, "classNameOverrides"),
    };

    if (!virtualizeColumns) {
      return (
        <tr key={row.id} {...trProps}>
          {row.getVisibleCells().map(renderBodyCell)}
        </tr>
      );
    }

    const visibleCells = row.getVisibleCells();
    const cellFor = (col: Column<T, unknown>) => {
      const idx = visibleColIndexById.get(col.id);
      return idx === undefined ? undefined : visibleCells[idx];
    };

    return (
      <tr key={row.id} {...trProps}>
        {stickyLeftColumns.map((c) => {
          const cell = cellFor(c);
          return cell ? renderBodyCell(cell) : null;
        })}
        <td aria-hidden style={{ width: colPaddingLeft, padding: 0 }} />
        {virtualCols.map((vc) => {
          const col = nonStickyColumns[vc.index];
          const cell = col ? cellFor(col) : undefined;
          return cell ? renderBodyCell(cell) : null;
        })}
        <td aria-hidden style={{ width: colPaddingRight, padding: 0 }} />
        {stickyRightColumns.map((c) => {
          const cell = cellFor(c);
          return cell ? renderBodyCell(cell) : null;
        })}
      </tr>
    );
  };

  const renderLoadingRow = (index: number) => {
    const skeletonCellFor = (column: Column<T, unknown>, key: string) => (
      <SkeletonCell
        key={key}
        hideSkeleton={Boolean(column.columnDef.meta?.hideSkeleton)}
        variant={variant}
      />
    );

    const baseProps = {
      className: twJoin(ROW_CLASS_NAMES, rowClassName, "cursor-default"),
      "data-loc": "table-loading-row",
    };

    if (!virtualizeColumns) {
      return (
        <tr key={index} {...baseProps}>
          {visibleColumns.map((column, i) =>
            skeletonCellFor(column, `${column.id}_${i}`),
          )}
        </tr>
      );
    }

    return (
      <tr key={index} {...baseProps}>
        {stickyLeftColumns.map((c, i) => skeletonCellFor(c, `sl_${c.id}_${i}`))}
        <td aria-hidden style={{ width: colPaddingLeft, padding: 0 }} />
        {virtualCols.map((vc) => {
          const col = nonStickyColumns[vc.index];
          return col ? skeletonCellFor(col, `v_${col.id}_${vc.index}`) : null;
        })}
        <td aria-hidden style={{ width: colPaddingRight, padding: 0 }} />
        {stickyRightColumns.map((c, i) =>
          skeletonCellFor(c, `sr_${c.id}_${i}`),
        )}
      </tr>
    );
  };

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: rows.length,
    estimateSize: () => 36,
    overscan: 10,
    enabled: !disableVirtualization,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalRowSize = rowVirtualizer.getTotalSize();
  // Dynamic top and botton padding is added to the table content to maintain the correct scroll bar size and position
  const paddingTop = virtualRows?.[0]?.start || 0;
  const paddingBottom =
    totalRowSize - (virtualRows?.[virtualRows.length - 1]?.end || 0);

  const debouncedOnScrolledToTheBottom = useDebouncedCallback(
    onScrolledToTheBottom ?? noop,
    1000,
    {
      leading: true,
      trailing: false,
    },
  );

  const onScroll = (containerRefElement?: HTMLDivElement | null) => {
    if (containerRefElement) {
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      if (scrollHeight - scrollTop - clientHeight < 1000) {
        debouncedOnScrolledToTheBottom();
      }
    }
  };

  const renderRows = () => {
    if (isLoading) {
      return times(loadingRowsCount, (i) => renderLoadingRow(i));
    }
    if (disableVirtualization) {
      return rows.map((row) => renderRow(row));
    }
    return virtualRows.map((virtualRow) => {
      const row = rows[virtualRow.index];
      return renderRow(row);
    });
  };

  // Show empty state if no rows and emptyState is provided
  if (rows.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  return (
    <div
      ref={tableContainerRef}
      className={twMerge(
        "relative isolate h-full",
        !noScroll && "decideScrollbar overflow-auto",
        frameClassName,
      )}
      data-loc={dataLoc}
      onScroll={(e) =>
        onScrolledToTheBottom ? onScroll(e.target as HTMLDivElement) : undefined
      }
    >
      <table
        className={twJoin(
          "border-collapse border-spacing-0 text-gray-800",
          virtualizeColumns ? "table-fixed" : "w-full table-auto",
          variant === "compact" && "border-gray-100 border-b",
        )}
        style={
          virtualizeColumns
            ? {
                width:
                  totalColSize +
                  stickyLeftColumns.reduce((s, c) => s + c.getSize(), 0) +
                  stickyRightColumns.reduce((s, c) => s + c.getSize(), 0),
              }
            : undefined
        }
      >
        <thead>{renderedHeaderGroups.map(renderHeaderGroup)}</thead>
        <tbody>
          {!disableVirtualization && paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {renderRows()}
          {isFetchingNextPage &&
            times(loadingRowsCount, (i) => renderLoadingRow(i))}
          {!isLoading && !disableVirtualization && paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom}px` }} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export const Header = <D extends object, V>({
  children,
  info,
  dataLoc,
}: {
  children?: React.ReactNode;
  info: HeaderContext<D, V>;
  dataLoc?: string;
}) => {
  if (!children) {
    // If no children we consider it as a placeholder and return null
    return null;
  }

  const variant = info.table.options.meta?.variant ?? "default";
  const sticky = info.column.columnDef.meta?.sticky;

  return (
    <div
      className={twJoin(
        "flex items-center whitespace-nowrap",
        "pr-2 pl-1.5",

        variant === "default" && "h-12",
        variant === "compact" && "h-8",

        sticky && "sticky z-20 bg-white",
        sticky === "left" && "left-0",
        sticky === "right" && "right-0",
      )}
      data-loc={dataLoc}
    >
      <div className="min-w-0 font-inter-medium-12px text-gray-800">
        {children}
      </div>
    </div>
  );
};

export const Cell = <D extends object, V>({
  children,
  className,
  dataLoc,
  info,
}: {
  children?: React.ReactNode;
  className?: string;
  dataLoc?: string;
  info: CellContext<D, V>;
}) => {
  const variant = info.table.options.meta?.variant ?? "default";
  const sticky = info.column.columnDef.meta?.sticky;

  return (
    <div
      className={twMerge(
        "flex items-center truncate font-inter-normal-12px text-gray-800",
        "pr-2 pl-1.5",

        variant === "default" && "h-12",
        variant === "compact" && "h-8 py-1.5",

        sticky && "sticky z-10 bg-white",
        sticky === "left" && "left-0",
        sticky === "right" && "right-0",
        className,
      )}
      data-loc={dataLoc}
    >
      {children}
    </div>
  );
};

/**
 * By default, this component allows to hide/show columns for given table.
 * But it is not stored anywhere.
 * If uiStoreKey is provided to the table component,
 * it will use the local storage to store the visibility state.
 */
export const ColumnVisibilityDropdown = <TData extends RowData>({
  dataLoc,
  table,
  min,
  max,
}: {
  dataLoc?: string;
  table: Table<TData>;
  min?: number;
  max?: number;
}) => {
  const columns = table
    .getAllLeafColumns()
    // Skip display columns, use only columns with actual data
    .filter((column) => column.accessorFn);
  const visibleColumns = table.getVisibleLeafColumns();
  const onChange = (value: string[]) => {
    table.setColumnVisibility(
      columns.reduce((acc, col) => {
        acc[col.id] = value.includes(col.id);
        return acc;
      }, {} as VisibilityState),
    );
  };

  return (
    <ColumnVisibilityDropdownBase
      dataLoc={dataLoc}
      items={columns.map((column) => ({
        label: column.columnDef.meta?.displayName ?? column.id,
        value: column.id,
        disabled: !column.getCanHide()
          ? "This column is required and cannot be hidden"
          : undefined,
      }))}
      max={max}
      min={min}
      value={visibleColumns.map((column) => column.id)}
      onChange={onChange}
      onReset={table.resetColumnVisibility}
    />
  );
};

const useVisibilityStore = (key: string | undefined, defaultValue = {}) => {
  const NOOP_STORE_KEY = "NOOP_STORE_KEY";
  const visibilityLocalStorage = useLocalStorage(
    key ?? NOOP_STORE_KEY,
    defaultValue,
  );
  const visibilityLocalState = useState(defaultValue);

  // If no key is provided, just use internal table state
  return key ? visibilityLocalStorage : visibilityLocalState;
};
